/* eslint-disable no-nested-ternary */
import { ThemeProvider } from "next-themes";
import { usePathname } from "next/navigation";
import { parseAsStringLiteral, useQueryState } from "nuqs";

import AppUpdateBanner from "components/layouts/AppUpdateBanner";
import Navbar from "components/navbar/Navbar";
import isDarkModeSupportedPage from "components/ux/isDarkModeSupportedPage";

import TrialStickyBanner from "components/billing/TrialStickyBanner";
import TwoPassRender from "components/shared/TwoPassRender";
import { Badge } from "components/ui/Badge";
import classNames from "components/ui/classNames";
import Twemoji from "components/ui/Twemoji";
import { getBooleanFlag } from "lib/flagging/FeatureFlags";
import useSelfie from "lib/hooks/useSelfie";
import Link from "next/link";
import { FormattedMessage } from "react-intl";
import { IUserSettingsV2Theme } from "types/graphqlTypes";

interface NavbarLayoutProps {
  forcedTheme?: "light" | "dark";
  hardRefresh?: boolean;
  disableStickyNavbar?: boolean;
  onlyChildren?: boolean;
  children: any;
}

export default function NavbarLayout({
  forcedTheme,
  hardRefresh,
  disableStickyNavbar = false,
  onlyChildren,
  children,
}: NavbarLayoutProps) {
  const viewer = useSelfie();
  const pathname = usePathname();
  const [currentThemeFromUrl] = useQueryState("theme", parseAsStringLiteral(["light", "dark"]).withDefault("light"));

  return (
    <ThemeProvider
      forcedTheme={(() => {
        if (forcedTheme) {
          return forcedTheme;
        }
        // We're not there yet
        if (!viewer && pathname === "/") {
          return "light";
        }
        if (!isDarkModeSupportedPage(pathname)) {
          return "light";
        }

        if (viewer) {
          return viewer?.settingsV2.theme === IUserSettingsV2Theme.Dark ? "dark" : "light";
        }

        return currentThemeFromUrl;
      })()}
      enableSystem={false}
      attribute="class"
      disableTransitionOnChange={true}
    >
      {onlyChildren ? (
        children
      ) : (
        <>
          <Navbar hardRefresh={hardRefresh} disableStickyNavbar={disableStickyNavbar} />
          {process.env.NODE_ENV === "production" && <AppUpdateBanner />}
          {viewer?.isTrialing && pathname !== "/thank-you" && <TrialStickyBanner />}

          {getBooleanFlag("features/isNewScreenerEnabled") && pathname === "/portfolio" && (
            <TwoPassRender>
              {() => {
                const didSeeScreener = localStorage.getItem("didSeeScreener");
                if (didSeeScreener) {
                  return null;
                }
                return (
                  <div className="mx-auto max-w-7xl pt-4">
                    <div className="rounded-2xl border border-sky-600/20 bg-sky-100 px-3.5 py-3.5 dark:border-slate-950 dark:bg-sky-700/40">
                      <div className="flex items-center gap-x-2">
                        <h4 className="inline-flex text-xl font-bold tracking-tighter text-sky-800 dark:text-sky-400">
                          <FormattedMessage defaultMessage="Planilhão" id="2FI5i6" />
                        </h4>
                        <Badge variant="blue">
                          <FormattedMessage defaultMessage="Novidade" id="yC7RnY" />
                        </Badge>
                        <Twemoji emoji="🥳" />
                      </div>
                      <p className="text-base font-medium text-slate-600 dark:font-bold dark:text-gray-300">
                        <FormattedMessage
                          defaultMessage="Acompanhe os principais indicadores de todos os ativos da sua carteira em um único lugar. Ações, FIIs, Stocks e REITs — tudo incluso, sem limitações ;)"
                          id="WbQs79"
                        />
                      </p>
                      <Link
                        href="/screener?list=portfolio"
                        className={classNames(
                          "mt-2 flex w-full items-center justify-center rounded-lg py-1.5 text-sm font-bold",
                          "border border-indigo-300/60 bg-indigo-100 text-indigo-600",
                          "hover:bg-indigo-200/80 hover:text-indigo-700",
                          "dark:bg-slate-700 dark:text-slate-300",
                          "dark:border-transparent dark:bg-sky-600/60 dark:text-sky-50",
                          "dark:hover:bg-sky-500/30 dark:hover:text-sky-100",
                        )}
                      >
                        <FormattedMessage defaultMessage="Acessar planilhão com meus ativos" id="MqY0Bw" />
                      </Link>
                    </div>
                  </div>
                );
              }}
            </TwoPassRender>
          )}

          {children}
        </>
      )}
    </ThemeProvider>
  );
}
